import { useMutation } from '@apollo/client/react';
import * as Sentry from '@sentry/react';
import {
  BodyText,
  Button,
  Flex,
  Link,
  TextInput,
} from '@utilitywarehouse/hearth-react';
import { ChevronRightSmallIcon } from '@utilitywarehouse/hearth-react-icons';
import { Formik, FormikHelpers, useFormikContext } from 'formik';
import { ChangeEvent, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Layout } from '@components/Layout';
import { ValidationErrorMessages } from '@constants/forms';
import { AUTH_RETURN_URL } from '@constants/queryParams';
import {
  APPOINTMENT_ID,
  APPOINTMENT_TOKEN,
  APPOINTMENT_USER_ID,
  APPOINTMENT_USER_TYPE,
  AUTO_JOIN,
} from '@constants/storageKeys';
import { useAttribution } from '@hooks/useAttribution';
import { useFieldProps } from '@hooks/useFieldProps';
import { getBaseUrl } from '@services/Auth/utils';
import { useSetAppointmentSession } from '@store/appointment/selectors';
import { useTrackingContext } from '@tracking/Provider/Context';
import { useTrackSignupRemoteAppointmentJoinedOnce } from '@tracking/trackingEvents';
import {
  joinRemoteAppointment as joinMutation,
  supportRemoteAppointment as supportMutation,
} from '@utils/queries/appointment';

import './styles.scss';
import { validationSchema } from './validationSchema';

interface JoinFormProps {
  isPartner: boolean;
}

interface JoinSectionValues {
  appointmentCode: string;
}

interface JoinProps {
  isPartner: boolean;
}

const JOIN_PAGE_CONTENT = {
  partner: {
    title: 'Support a remote appointment',
    explanation:
      "Ask the Partner you're supporting for the appointment code, and enter it below to join their appointment.",
  },
  customer: {
    title: 'Join Your Appointment',
    explanation:
      'Your UW Partner will give you a code to enter below, so you can join your appointment.',
  },
} as const;

const JoinForm = ({ isPartner }: JoinFormProps) => {
  const {
    values,
    setFieldValue,
    errors,
    touched,
    handleChange,
    handleBlur,
    submitCount,
  } = useFormikContext<JoinSectionValues>();

  const getFieldProps = useFieldProps<JoinSectionValues>({
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    submitCount,
  });

  const handleCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFieldValue('appointmentCode', event.target.value.toUpperCase());
  };

  const supportLoginHandler = () => {
    let returnPath = '/remote/join';

    if (values.appointmentCode) {
      sessionStorage.setItem(AUTO_JOIN, 'true');
      returnPath = `${getBaseUrl()}/remote/join/${values.appointmentCode}`;
    }
    window.location.href = `${getBaseUrl()}/auth/okta/external/login?${AUTH_RETURN_URL}=${encodeURIComponent(returnPath)}`;
  };

  return (
    <Flex direction="column" spacing="xl">
      <TextInput
        {...getFieldProps('appointmentCode')}
        onChange={handleCodeChange}
        label="Appointment code"
        required
      />
      <Flex
        direction={{
          tablet: 'row',
          mobile: 'column',
        }}
      >
        <Button type="submit" size="md">
          {isPartner ? 'Join as supporting Partner' : 'Join appointment'}
        </Button>
      </Flex>
      <Flex justifyContent={{ tablet: 'start', mobile: 'center' }}>
        {!isPartner && (
          <Link asChild>
            <button type="button" onClick={supportLoginHandler}>
              Login as a supporting Partner
              <ChevronRightSmallIcon />
            </button>
          </Link>
        )}
      </Flex>
    </Flex>
  );
};

const Join = ({ isPartner }: JoinProps) => {
  const { code } = useParams();
  const navigate = useNavigate();
  const setAppointmentSession = useSetAppointmentSession();
  const { addAttributionDetails } = useAttribution();
  const trackAppointmentJoined = useTrackSignupRemoteAppointmentJoinedOnce();
  const initialValues: JoinSectionValues = {
    appointmentCode: code ?? '',
  };

  const content = isPartner
    ? JOIN_PAGE_CONTENT.partner
    : JOIN_PAGE_CONTENT.customer;

  const [joinRemoteAppointment] = useMutation(joinMutation);
  const [supportRemoteAppointment] = useMutation(supportMutation);
  const { trackingContexts } = useTrackingContext();
  const autoJoin = sessionStorage.getItem(AUTO_JOIN) === 'true';
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (isPartner && code && autoJoin && formRef.current) {
      sessionStorage.removeItem(AUTO_JOIN);
      formRef.current.requestSubmit();
    }
  }, [isPartner, code, autoJoin]);

  const handleSubmit = async (
    values: JoinSectionValues,
    { setSubmitting, setFieldError }: FormikHelpers<JoinSectionValues>
  ) => {
    try {
      const mutation = isPartner
        ? supportRemoteAppointment
        : joinRemoteAppointment;
      const { data } = await mutation({
        variables: {
          id: values.appointmentCode,
        },
      });

      const responseData =
        data && 'supportRemoteAppointment' in data
          ? data.supportRemoteAppointment
          : data?.joinRemoteAppointment;

      if (responseData) {
        const { me, appointment } = responseData;
        localStorage.setItem(APPOINTMENT_USER_ID, me.id);
        localStorage.setItem(APPOINTMENT_TOKEN, me.token);
        localStorage.setItem(APPOINTMENT_ID, appointment.id);
        localStorage.setItem(APPOINTMENT_USER_TYPE, me.type);

        setAppointmentSession({
          appointmentId: appointment.id,
          userId: me.id,
          userType: me.type,
        });

        Sentry.setTag('appointment_id', appointment.id);

        if (isPartner && appointment.basketId) {
          const supportingPartner = appointment.participants.find(
            (participant) => participant.id === me.id
          );
          if (supportingPartner?.partnerId) {
            await addAttributionDetails(appointment.basketId, {
              supportingPartner: {
                partner: {
                  id: supportingPartner.partnerId,
                  positionId: me.id,
                },
              },
            });
          }
        }

        trackAppointmentJoined({}, { contexts: trackingContexts });

        // TODO: navigate to the prejoin screen, move
        // navigating to current appointment synced path
        // to the lobby page submit
        navigate(appointment.path);
      }
    } catch {
      setFieldError(
        'appointmentCode',
        ValidationErrorMessages.INVALID_APPOINTMENT_CODE
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout title={content.title}>
      <section className="wrapper">
        <Flex
          direction="column"
          spacing="xl"
          className="join__container"
          paddingTop="100"
        >
          <BodyText>{content.explanation}</BodyText>
          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
          >
            {({ handleSubmit }) => (
              <form onSubmit={handleSubmit}>
                <JoinForm isPartner={isPartner} />
              </form>
            )}
          </Formik>
        </Flex>
      </section>
    </Layout>
  );
};

export { Join };
